/*
 * Copyright (C) 2019 - present Marek Kuzora - All Rights Reserved.
 */


import BorderType from 'solis/react/board/geometry/border.type';

import * as Hexagon from 'solis/type/point/hexagon';
import * as Point from 'solis/type/point';


const EPS = 1e-10;


export default class
{
  constructor (index, currentTriangle, externalTriangle, zoneData, hexGeometry)
  {
    this.index = index;

    this.currentTriangle = currentTriangle;
    this.externalTriangle = externalTriangle;

    this.zoneData = zoneData;
    this.hexGeometry = hexGeometry;
  }


  getIndex ()
  {
    return this.index;
  }


  getNeighbor ()
  {
    return this.zoneData.getNeighbor(this.index);
  }


  getType ()
  {
    const neighbor = this.getNeighbor();

    if (this.zoneData.isExternalBorder(neighbor))
    {
      return this.zoneData.isTemporaryBorder(neighbor) ? BorderType.Temporary : BorderType.External;
    }

    if (this.zoneData.isJaggedBorder(neighbor))
    {
      return BorderType.Jagged;
    }

    if (neighbor.isJaggedBorder(this.zoneData))
    {
      return BorderType.None;
    }

    if (this.hexGeometry.isGroupVisible() && this.zoneData.isGroupBorder(neighbor))
    {
      return BorderType.Group;
    }

    if (this.hexGeometry.isZoneVisible())
    {
      return BorderType.Zone;
    }

    return BorderType.None;
  }


  getWidth ()
  {
    switch (this.getType())
    {
      case BorderType.External:       return this.hexGeometry.getExternalBorderWidth();
      case BorderType.Temporary:      return this.hexGeometry.getExternalBorderWidth();
      case BorderType.Group:          return this.hexGeometry.getGroupBorderWidth();
      case BorderType.Zone:           return this.hexGeometry.getZoneBorderWidth();
      case BorderType.Jagged:         return this.hexGeometry.getJaggedBorderWidth();

      default:                        return 0;
    }
  }


  getPoints ()
  {
    return this.currentTriangle;
  }


  getExternalPoints ()
  {
    return this.externalTriangle;
  }


  getOutlinePoints ()
  {
    const [ start, end ] = this.currentTriangle;

    const neighbor = this.getNeighbor();

    if (this.zoneData.isJaggedBorder(neighbor))
    {
      return this.getJaggedPoints(start, end);
    }

    if (neighbor && neighbor.isJaggedBorder(this.zoneData))
    {
      return this.getJaggedPoints(end, start).reverse();
    }

    return [ start, end ];
  }


  getJaggedPoints (start = this.currentTriangle[0], end = this.currentTriangle[1])
  {
    const step = this.hexGeometry.getJaggedStep();
    const amplitude = this.hexGeometry.getJaggedAmplitude();

    return Point.getJaggedPoints(Point.distance(start, end), step, amplitude).map(point =>
    {
      return Point.transform(point, start, Point.getLineAngleRadians(start, end));
    });
  }


  getRotatedPoints (triangle = this.currentTriangle)
  {
    const partitionLengths = this.getPartitionLengths();
    const centerDistance = this.getCenterToPartitionDistance();

    return [ { x: 0, y: partitionLengths[0] }, { x: 0, y: - partitionLengths[1] }, { x: centerDistance, y: 0 } ];
  }


  getCenter (triangle = this.currentTriangle)
  {
    return triangle[2];
  }


  getPartitionPoint (triangle = this.currentTriangle)
  {
    return Point.getPerpendicularIntersection(...triangle);
  }


  getCenterToPartitionDistance (triangle = this.currentTriangle)
  {
    return Point.distance(this.getPartitionPoint(triangle), this.getCenter(triangle))
  }


  getCurrentToExternalDistance ()
  {
    return this.getCenterToPartitionDistance(this.externalTriangle) - this.getCenterToPartitionDistance(this.currentTriangle);
  }


  getLength (triangle = this.currentTriangle)
  {
    return Point.distance(triangle[0], triangle[1])
  }


  getPartitionLengths (triangle = this.currentTriangle)
  {
    const partition = this.getPartitionPoint(triangle);

    return [ Point.distance(triangle[0], partition), Point.distance(triangle[1], partition) ];
  }


  getRotationDegrees ()
  {
   return ((this.index + 2) % Hexagon.getPointCount()) * 60;
  }


  getDasharray (count)
  {
    return this.getLength(this.currentTriangle) / count;
  }


  getDashoffset ()
  {
    return 0;
  };


  // QQ different version of dasharray - gap space stays exactly the same but we trim at the edges.
  // It breaks for external dashed border since that's internalTriangle and dashes don't meet at seams.
  getDasharrayAlternative (count)
  {
    return this.getLength(this.externalTriangle) / count;
  }


  getDashoffsetAlternative ()
  {
    const currentLength = Point.distance(this.currentTriangle[0], this.getPartitionPoint(this.currentTriangle));
    const externalLength = Point.distance(this.externalTriangle[0], this.getPartitionPoint(this.externalTriangle));

    return externalLength - currentLength;
  };


};
